import React from 'react';

type LogoVariant =
  | 'symbol-blue'
  | 'symbol-beige'
  | 'symbol-light-gradient'
  | 'logotype-blue'
  | 'logotype-beige'
  | 'logotype-light-gradient';

interface MaieLogomarkProps {
  /** Choose the asset variant that contrasts with your surface. */
  variant?: LogoVariant;
  /** Height in px; width is auto to preserve aspect ratio. */
  size?: number;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
  /**
   * When set on a `-blue` variant, render the blue mark on light surfaces and
   * automatically swap to the `-light-gradient` mark on dark ones (the blue
   * disappears on dark backgrounds — e.g. the "Powered by" sidebar footer).
   * Unlike a CSS-only `dark:` swap, this keys off the *effective surface* —
   * the `dark` class, the `theme-dark-mode` body class, OR the luminance of the
   * resolved `--color-bg-secondary` — so it works on custom dark tenant
   * palettes that never set the `dark` class.
   */
  autoDark?: boolean;
}

const ASSET_PATH: Record<LogoVariant, string> = {
  'symbol-blue': '/brand/symbol-blue.png',
  'symbol-beige': '/brand/symbol-beige.png',
  'symbol-light-gradient': '/brand/symbol-light-gradient.png',
  'logotype-blue': '/brand/logotype-blue.png',
  'logotype-beige': '/brand/logotype-beige.png',
  'logotype-light-gradient': '/brand/logotype-light-gradient.png',
};

/** Relative luminance (0 dark … 1 light) of a hex/rgb color, or null. */
function luminanceOf(color: string): number | null {
  const c = color.trim();
  let r: number, g: number, b: number;
  const hex = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3)
      h = h
        .split('')
        .map((x) => x + x)
        .join('');
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else {
    const m = c.match(/rgba?\(([^)]+)\)/i);
    if (!m) return null;
    const parts = m[1].split(',').map((p) => parseFloat(p));
    [r, g, b] = parts;
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  }
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/** True when the app's current surface reads as dark (any theme mechanism). */
function computeIsDarkSurface(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;
  if (root.classList.contains('dark')) return true;
  if (document.body.classList.contains('theme-dark-mode')) return true;
  const bg = getComputedStyle(root)
    .getPropertyValue('--color-bg-secondary')
    .trim();
  const lum = luminanceOf(bg);
  return lum != null ? lum < 0.5 : false;
}

/**
 * Tracks whether the current surface is dark, reacting to theme switches: the
 * `dark` class on <html>, the `theme-*` class on <body> (custom tenant
 * palettes), and OS scheme changes.
 */
function useIsDarkSurface(): boolean {
  const [dark, setDark] = React.useState(computeIsDarkSurface);
  React.useEffect(() => {
    const update = () => setDark(computeIsDarkSurface());
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    mq?.addEventListener?.('change', update);
    return () => {
      obs.disconnect();
      mq?.removeEventListener?.('change', update);
    };
  }, []);
  return dark;
}

/**
 * MAIE brand logo. Wraps the real brand assets copied from MAIE.Landing.
 * Use `symbol-*` for the mark-only variant and `logotype-*` for the
 * horizontal wordmark. Default is `symbol-blue` on light surfaces.
 */
export const MaieLogomark: React.FC<MaieLogomarkProps> = ({
  variant = 'symbol-blue',
  size = 28,
  className,
  title = 'MAIE',
  style,
  autoDark,
}) => {
  const isDark = useIsDarkSurface();
  // `width: auto` + `maxWidth: 100%` + `objectFit: contain` keeps the wordmark
  // from overflowing (and being clipped by) a narrow sidebar column.
  const imgStyle: React.CSSProperties = {
    height: size,
    width: 'auto',
    maxWidth: '100%',
    objectFit: 'contain',
    display: 'block',
    ...style,
  };

  const resolved: LogoVariant =
    autoDark && isDark && variant.endsWith('-blue')
      ? (variant.replace('-blue', '-light-gradient') as LogoVariant)
      : variant;

  return (
    <img
      src={ASSET_PATH[resolved]}
      alt={title}
      height={size}
      style={imgStyle}
      className={className}
      draggable={false}
    />
  );
};
